/* eslint-disable @typescript-eslint/no-explicit-any */
import { Heading, useToast, VStack, Checkbox, Text, Link, useDisclosure, Spacer, Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/atoms/Button';
import { useCallback, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Input } from '../../../components/atoms/Input';
import { HiOutlineMailOpen } from 'react-icons/hi';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useMutation } from 'react-query';
import { Footer } from '../../../components/organisms/Footer';
import { ShowPasswordToggle } from '@/components/atoms/ShowPasswordToggle';
import { useProfile } from '@/contexts/ProfileProvider';
import { vinculateExternalCode, validatePromocode, vinculateCPF, registerUser } from '@/service/auth/tokens';
import { isAuthenticated, setAccessToken } from '@/service/axios';
import { useAPICallAsMutation } from '@/service/hooks';
import { masks } from '@/utils/masks';
import { validarCPF } from '@/utils/validateDocuments';
import { ModalCPF } from '@/components/organisms/ModalCPF';
import { ICodeCheckResponse, IPlan } from '@/service/types';

interface IUserRegistrationData {
  full_name: string;
  email: string;
  telephone: string;
  cpf: string;
  password: string;
  promocode?: string;
  accessCode?: string;
  external_code?: string;
}

interface VinculatePlan {
  id: number;
  plan: IPlan;
  plan_children: IPlan[];
}

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
}

function isAlreadyRegisteredError(data: Record<string, unknown> | undefined): boolean {
  if (!data) return false;
  return Boolean(data.email || data.cpf);
}

function isTimeoutOrNetworkError(error: any): boolean {
  if (!error) return false;
  if (error.code === 'ECONNABORTED') return true;
  const message = String(error.message || '').toLowerCase();
  return message.includes('timeout') || (error.request && !error.response);
}

export function RegisterForm({ onSwitchToLogin }: RegisterFormProps = {}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { id, colors, cpfModule, subscriptionModule, product, promocodeModule, moduleExternalCode, metadata } = useTheme();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registerType, setRegisterType] = useState<'promocode' | 'cpf' | 'subscription' | 'externalCode' | null>(null);

  const { updateProfile } = useProfile();
  const [cnpjValue, setCnpjValue] = useState<string | null>(null);
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [validatedAccessCode, setValidatedAccessCode] = useState<string | null>(null);
  const [activatedPlan, setActivatedPlan] = useState<VinculatePlan | null>(null);

  const modalCpf = useDisclosure();

  if (isAuthenticated()) navigate('/');

  const passwordSchema = z
    .string()
    .min(8, { message: 'A senha deve ter pelo menos 8 caracteres.' })
    .regex(/[A-Z]/, { message: 'A senha deve conter pelo menos uma letra maiúscula.' })
    .regex(/[a-z]/, { message: 'A senha deve conter pelo menos uma letra minúscula.' })
    .regex(/[0-9]/, { message: 'A senha deve conter pelo menos um número.' })
    .regex(/[^a-zA-Z0-9]/, { message: 'A senha deve conter pelo menos um caractere especial.' });

  const formSchema = z.object({
    full_name: z.string().min(1),
    email: z.string().email(),
    cpf: z.string().refine((value) => {
      return validarCPF(value);
    }),
    password: passwordSchema,
  });

  const methods = useForm<IUserRegistrationData>({
    resolver: zodResolver(formSchema),
  });

  const { control, watch, handleSubmit, setValue, reset } = methods;

  const accessCode = watch('accessCode');

  const accessCodeLabel = useMemo(() => {
    if (registerType === 'promocode') return 'Código Promocional';
    else if (registerType === 'cpf') return 'CPF / CNPJ';

    return 'Código de Acesso';
  }, [registerType]);

  const validatorFunc = useCallback(async () => {
    if (!id) return false;
    if (!accessCode) return false;

    if (registerType === 'externalCode') {
      return vinculateExternalCode(accessCode, id, product?.id);
    }

    if (registerType === 'promocode') return validatePromocode(accessCode);
    else if (registerType === 'cpf') {
      return vinculateCPF(accessCode, id, product?.id);
    }
  }, [registerType, id, accessCode]);

  const validatorMutation = useMutation(() => validatorFunc() as Promise<ICodeCheckResponse>, {
    onSuccess(data: any) {
      if (accessCode) {
        setValidatedAccessCode(accessCode);
      }
      reset();
      if (registerType === 'promocode' && data?.status === false) {
        toast({
          title: 'Oops!',
          description: `Parece que este ${accessCodeLabel} não é valido. Por favor verifique se está correto.`,
          status: 'error',
        });
        return;
      }

      if (data?.first_signup && data?.is_cnpj) {
        modalCpf.onOpen();
        setCnpjValue(accessCode || '');
      }
      if (data?.is_cnpj && data?.document_for_cnpj) {
        setValue('cpf', masks.cpf(data?.document_for_cnpj));
      }

      if (registerType === 'cpf' && accessCode) {
        setValue('cpf', accessCode);
      }

      if (registerType === 'promocode' && data?.agreement) {
        setActivatedPlan({
          id: data.agreement.id,
          plan: { display_name: data.agreement.name } as IPlan,
          plan_children: data.plans,
        });
      } else if (data?.plan || (data?.plan_children?.length ?? 0) > 0) {
        setActivatedPlan(data);
      }

      if (accessCode) {
        toast({
          title: 'Sucesso!',
          description: `Código de Acesso ${accessCode} validado com sucesso!, realize o cadastro.`,
          status: 'success',
        });
        setAccessCodeValidated(true);
      }
    },
    onError(err: any) {
      let errorMessage = `Parece que este ${accessCodeLabel} não é valido. Por favor verifique se está correto.`;
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      toast({
        title: 'Oops!',
        description: errorMessage,
        status: 'error',
      });
    },
  });

  const registerMutation = useAPICallAsMutation(
    (data: IUserRegistrationData) => {

      if (registerType === 'promocode') {
        data.promocode = validatedAccessCode || undefined;
      }
      delete data.accessCode;
      if (registerType === 'externalCode') {
        data.external_code = validatedAccessCode ? masks.unmask(validatedAccessCode) : undefined;
      }
      return registerUser(data, id || 0);
    },
    {
      onSuccess(data) {
        setAccessToken(data.token);
        updateProfile({});
        navigate('/');
      },
      onError(error: any) {
        const responseData = error?.response?.data as Record<string, unknown> | undefined;

        if (isAlreadyRegisteredError(responseData)) {
          toast({
            title: 'Cadastro realizado!',
            description:
              'Sua conta já está cadastrada. Tente fazer login com o e-mail e a senha que você acabou de informar.',
            status: 'success',
            duration: 8000,
            isClosable: true,
          });
          onSwitchToLogin?.();
          return;
        }

        if (isTimeoutOrNetworkError(error)) {
          toast({
            title: 'Tempo esgotado',
            description:
              'A requisição demorou muito para responder e foi cancelada. Verifique sua conexão e tente novamente.',
            status: 'warning',
            duration: 6000,
            isClosable: true,
          });
          return;
        }

        let message = 'Erro ao criar conta. Revise as informações do formulário e tente novamente';
        if (responseData?.promocode) {
          message = Array.isArray(responseData.promocode)
            ? String(responseData.promocode[0])
            : String(responseData.promocode);
        } else if (typeof responseData?.error === 'string') {
          message = responseData.error;
        } else if (typeof responseData?.detail === 'string') {
          message = responseData.detail;
        }

        toast({
          title: 'Oops!',
          description: message,
          status: 'error',
        });
      },
    }
  );

  if (!registerType)
    return (
      <VStack align={'flex-start'} width={'100%'} spacing={5}>
        <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'28px'}>
          Registrar com:
        </Heading>
        {cpfModule && <Button onClick={() => setRegisterType('cpf')}>{'CPF/CNPJ Vinculado'}</Button>}
        {promocodeModule && <Button onClick={() => setRegisterType('promocode')}>Código Promocional</Button>}
        {subscriptionModule !== false && <Button onClick={() => setRegisterType('subscription')}>Criar conta</Button>}
        {moduleExternalCode && (
          <Button onClick={() => setRegisterType('externalCode')}>
            {metadata?.buttonsubscriptionexternalcode || 'Compra com código externo'}
          </Button>
        )}

        <Text
          color="#747474"
          margin="auto"
          textAlign={'center'}
          alignItems={'center'}
          gap={1}
          fontSize={14}
          fontWeight={400}
        >
          Ao continuar você está de acordo com nossos{'  '}
          <span
            onClick={() => navigate('/legal/termos-de-uso')}
            style={{ cursor: 'pointer', color: colors.primaryColor }}
          >
            Termos de Uso
          </span>
          {'  '}e{'  '}
          <span
            onClick={() => navigate('/legal/politica-de-privacidade')}
            style={{ cursor: 'pointer', color: colors.primaryColor }}
          >
            Política de Privacidade
          </span>
        </Text>
      </VStack>
    );
  if (!accessCodeValidated && registerType !== 'subscription')
    return (
      <VStack align={'flex-start'} spacing={5} width={'100%'}>
        <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'28px'}>
          {accessCodeLabel}
        </Heading>
        {registerType === 'promocode' ? (
          <Input label="Código" control={control} name="accessCode" autoComplete="off" />
        ) : registerType === 'externalCode' ? (
          <Input
            control={control}
            name="accessCode"
            mask={metadata?.masksubscriptionexternalcode === 'phone' ? 'cell' : undefined}
            label={metadata.buttonsubscriptionexternalcode}
            autoComplete="off"
          />
        ) : (
          <Input control={control} name="accessCode" label="CPF/CNPJ" mask="document" autoComplete="off" />
        )}

        <Button onClick={() => validatorMutation.mutate()} isLoading={validatorMutation.isLoading}>
          Verificar
        </Button>
        <Link
          onClick={() => {
            setValue('accessCode', '');
            setRegisterType(null);
            setAccessCodeValidated(false);
            setValidatedAccessCode(null);
          }}
          margin={'auto'}
          color={colors.primaryColor}
        >
          Voltar
        </Link>
      </VStack>
    );

  return (
    <VStack w={'100%'} align={'flex-start'} spacing={4}>
      {activatedPlan ? (
        <VStack
          padding={'24px'}
          align={'flex-start'}
          width={'100%'}
          borderRadius={'16px'}
          backgroundColor="primaryColor"
          spacing={4}
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
        >
          <Heading color="white" fontSize={'24px'} fontWeight={600} letterSpacing="-0.02em">
            🎉 Parabéns!
          </Heading>

          <VStack align="flex-start" spacing={3} width="100%">
            <Text color="white" fontSize={'16px'} fontWeight={500} opacity={0.9}>
              Seu {registerType === 'cpf' ? 'CPF' : registerType === 'promocode' ? 'Promocode' : 'Código'} garante
              acesso a:
            </Text>

            <VStack align="flex-start" spacing={2} pl={4} width="100%">
              {activatedPlan?.plan && <Text color="white" fontSize={'15px'} fontWeight={600} display="flex" alignItems="center" gap={2}>
                ✓ {activatedPlan?.plan?.display_name}
              </Text>}

              {activatedPlan?.plan_children?.map((child, index) => (
                <Text
                  key={index}
                  color="white"
                  fontSize={'15px'}
                  fontWeight={600}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  ✓ {child.display_name}
                </Text>
              ))}
            </VStack>
          </VStack>
        </VStack>
      ) : (
        <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'36px'}>
          Cadastre-se
        </Heading>
      )}
      <form
        style={{ width: '100%' }}
        onSubmit={handleSubmit((data) => registerMutation.mutate(data as IUserRegistrationData))}
      >
        <VStack spacing={7} w={'100%'} align={'center'}>
          <Input control={control} name="full_name" label="Nome" autoComplete="off" />
          <Input control={control} name="cpf" label="CPF" mask="cpf" autoComplete="off" />
          <Input
            control={control}
            autoComplete="off"
            name="email"
            label="Email"
            rightElement={<HiOutlineMailOpen color="#292927" />}
          />
          <Input
            control={control}
            name="password"
            label="Criar Senha"
            autoComplete="new-password"
            type={showPassword ? 'text' : 'password'}
            rightElement={<ShowPasswordToggle value={showPassword} onChange={setShowPassword} />}
          />
          <Box
            w="100%"
            bg="transparent"
            borderRadius="8px"
            px={3}
            py={2}
          >
            <Checkbox color="#292927" isChecked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}>
              Concordo com os{' '}
              <span
                onClick={() => navigate('/legal/termos-de-uso')}
                style={{ cursor: 'pointer', color: colors.primaryColor, fontWeight: 600 }}
              >
                Termos de Uso
              </span>
              {' '}e{' '}
              <span
                onClick={() => navigate('/legal/politica-de-privacidade')}
                style={{ cursor: 'pointer', color: colors.primaryColor, fontWeight: 600 }}
              >
                Política de Privacidade
              </span>
            </Checkbox>
          </Box>

          <Button isDisabled={!acceptedTerms} isLoading={registerMutation.isLoading} type="submit">
            CRIAR CONTA
          </Button>

          <Link
            onClick={() => {
              setValue('accessCode', '');
              setRegisterType(null);
              setAccessCodeValidated(false);
            }}
            color={colors.primaryColor}
          >
            Voltar
          </Link>

          <Footer />
          <Spacer
            mb={{
              base: '50px',
              md: '0px',
            }}
          />
        </VStack>
      </form>
      <ModalCPF
        document={cnpjValue || undefined}
        isOpen={modalCpf.isOpen}
        onClose={(cpf) => {
          setValue('cpf', cpf);
          modalCpf.onClose();
        }}
      />
    </VStack>
  );
}
